<template>
  <UIDropdownColumns
    v-if="!hideFieldsSettings"
    v-model="visibleFields"
    :data="fieldsOptions"
    placement="bottom-end"
    disallow-drag
  >
    <template #default="{ show }">
      <UIButton
        variant="gray"
        fill="outlined"
        size="sm"
        :leading="Cog6ToothIcon"
        :disabled="readonly || loading"
        @click="show"
      />
    </template>
  </UIDropdownColumns>
  <UIButton
    v-tooltip.top="'Zoom in'"
    size="sm"
    :leading="MagnifyingGlassPlusIcon"
    variant="gray"
    fill="outlined"
    :disabled="loading"
    @click="orgChartRef?.zoomIn"
  />
  <UIButton
    v-tooltip.top="'Zoom out'"
    size="sm"
    :leading="MagnifyingGlassMinusIcon"
    variant="gray"
    fill="outlined"
    :disabled="loading"
    @click="orgChartRef?.zoomOut"
  />
  <UIButton
    v-tooltip.top="'Reset zoom'"
    size="sm"
    :leading="ViewfinderCircleIcon"
    variant="gray"
    fill="outlined"
    :disabled="loading"
    @click="orgChartRef?.resetZoom"
  />
  <UIButton
    v-tooltip.top="collapseTooltip"
    :leading="collapseIcon"
    size="sm"
    variant="gray"
    fill="outlined"
    :disabled="loading"
    @click="handleToggleCollapse"
  />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { OrgChart } from '@moonwane/vue3-org-chart'

import { UIButton } from '@ui/buttons'
import { Cog6ToothIcon, MagnifyingGlassMinusIcon, MagnifyingGlassPlusIcon, ViewfinderCircleIcon } from '@heroicons/vue/24/outline'
import CollapseAllIcon from '@/assets/images/icons/collapse-all.svg'
import ExpandAllIcon from '@/assets/images/icons/expand-all.svg'
import { UIDropdownColumns } from '@ui/dropdowns'
import { ORGANIZATION_CHART_FIELDS } from './utils/const'

type Props = {
  orgChartRef: typeof OrgChart.prototype.data
  readonly?: boolean
  loading?: boolean
  hideFieldsSettings?: boolean
}

const props = defineProps<Props>()

defineOptions({ inheritAttrs: false })

const hiddenFields = defineModel<string[]>('hiddenFields')

const isCollapsed = ref(true)

const innerHiddenFields = ref<string[] | undefined>(hiddenFields.value)

const fieldsOptions = computed(() => ORGANIZATION_CHART_FIELDS.map(field => ({
  label: `${field.charAt(0).toUpperCase()}${field.slice(1)}`,
  value: field,
})))

const fieldValues = computed(() => fieldsOptions.value.map(item => item.value))

const visibleFields = computed({
  get() {
    const excludedFields = innerHiddenFields.value || hiddenFields.value
    return fieldValues.value?.filter(field => !excludedFields?.includes(field))
  },
  set(value) {
    const result = fieldValues.value?.filter(field => !value.includes(field))
    innerHiddenFields.value = result
    hiddenFields.value = result
  },
})

const collapseTooltip = computed(() =>
  isCollapsed.value
    ? 'Expand all'
    : 'Collapse all',
)

const collapseIcon = computed(() =>
  isCollapsed.value
    ? ExpandAllIcon
    : CollapseAllIcon,
)

const handleToggleCollapse = () => {
  if (isCollapsed.value) {
    props.orgChartRef?.expandAll()
  } else {
    props.orgChartRef?.collapseAll()
  }
  isCollapsed.value = !isCollapsed.value
}
</script>
